/**
 * Transform binding chip — one per axis of the Position / Scale rows.
 *
 * Shows whether the axis of the object's base transform is bound to a number
 * property (`{deckHeight}`) and opens a menu to bind it to any number property
 * declared on the object or an ancestor, rebind, or unbind (bake the resolved
 * value as a literal). Same shape as the colour chip on material slots; all
 * writes go through the shared trace-viewer editor helpers (one undo entry).
 *
 * The menu renders through a portal at a fixed position: the Transform
 * section clips overflow, and a menu inside it would be cut off by the next
 * behavior group.
 */

import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import {
    listBindableNumberProperties,
    bindTransformComponent,
    unbindTransformComponent,
} from 'trace-viewer/editor';
import type { BindableNumberProperty, BindableTransformChannel, TransformAxis } from 'trace-viewer/editor';
import type { SceneObject } from 'trace-viewer/scene';

export interface TransformBindingChipProps {
    sceneObject: SceneObject;
    channel: BindableTransformChannel;
    axis: TransformAxis;
    /** Property the axis is bound to, or null when it is a literal. */
    boundTo: string | null;
    /** Called after any bind/unbind so the row re-reads. */
    onChanged: () => void;
}

const menuItem: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '4px 8px', fontSize: '11px', color: '#ccc',
    cursor: 'pointer', whiteSpace: 'nowrap', background: 'transparent',
    border: 'none', width: '100%', textAlign: 'left',
};

export const TransformBindingChip: React.FC<TransformBindingChipProps> = ({
    sceneObject, channel, axis, boundTo, onChanged,
}) => {
    const [open, setOpen] = useState(false);
    const [menuPos, setMenuPos] = useState<{ left: number; top: number } | null>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    // Anchor the portaled menu under the chip; keep it on screen.
    useLayoutEffect(() => {
        if (!open) { setMenuPos(null); return; }
        const rect = buttonRef.current?.getBoundingClientRect();
        if (!rect) return;
        const menuHeight = menuRef.current?.offsetHeight ?? 0;
        const menuWidth = menuRef.current?.offsetWidth ?? 0;
        const top = rect.bottom + 3 + menuHeight > window.innerHeight ? rect.top - 3 - menuHeight : rect.bottom + 3;
        const left = Math.max(4, Math.min(rect.left, window.innerWidth - menuWidth - 4));
        setMenuPos({ left, top });
    }, [open]);

    // Close on click-away — the menu lives in a portal, so test both nodes.
    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent): void => {
            const target = e.target as Node;
            if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) return;
            setOpen(false);
        };
        window.addEventListener('mousedown', onDown);
        return () => window.removeEventListener('mousedown', onDown);
    }, [open]);

    const after = useCallback((): void => { setOpen(false); onChanged(); }, [onChanged]);

    const bind = (name: string): void => {
        if (name === boundTo) { setOpen(false); return; }
        void bindTransformComponent(sceneObject, channel, axis, name).then(({ errors }) => {
            if (errors.length > 0) console.warn('[TransformBindingChip] bind failed:', errors);
            after();
        });
    };

    const unbind = (): void => {
        void unbindTransformComponent(sceneObject, channel, axis).then(({ errors }) => {
            if (errors.length > 0) console.warn('[TransformBindingChip] unbind failed:', errors);
            after();
        });
    };

    const options: BindableNumberProperty[] = open ? listBindableNumberProperties(sceneObject) : [];
    const axisName = 'xyz'[axis];

    return (
        <div style={{ display: 'inline-flex' }}>
            <button
                ref={buttonRef}
                onClick={() => setOpen(o => !o)}
                title={boundTo
                    ? `${channel} ${axisName} follows "${boundTo}" — click to rebind or unbind`
                    : `Bind ${channel} ${axisName} to a number property`}
                style={{
                    fontSize: '9px', lineHeight: '12px',
                    color: boundTo ? '#b794f6' : '#666',
                    backgroundColor: boundTo ? '#b794f620' : 'transparent',
                    border: boundTo ? '1px solid #b794f640' : '1px solid transparent',
                    padding: '0 3px', borderRadius: '3px', cursor: 'pointer',
                    whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center',
                }}
            >
                {boundTo ? `$${boundTo}` : '⋯'}
            </button>

            {open && createPortal(
                <div
                    ref={menuRef}
                    style={{
                        position: 'fixed', left: menuPos?.left ?? 0, top: menuPos?.top ?? 0, zIndex: 1000,
                        visibility: menuPos ? 'visible' : 'hidden',
                        minWidth: '150px', maxHeight: '220px', overflowY: 'auto',
                        backgroundColor: '#252525', border: '1px solid #444', borderRadius: '4px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.4)', padding: '3px 0',
                    }}
                >
                    {options.length === 0 && (
                        <div style={{ ...menuItem, color: '#666', cursor: 'default', fontStyle: 'italic' }}>
                            No number properties
                        </div>
                    )}
                    {options.map(opt => {
                        const active = opt.name === boundTo;
                        return (
                            <button
                                key={opt.name}
                                onClick={() => bind(opt.name)}
                                style={{ ...menuItem, color: active ? '#fff' : '#ccc' }}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#333')}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                            >
                                <span style={{ width: '10px', color: '#b794f6' }}>{active ? '✓' : ''}</span>
                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{opt.label}</span>
                                {opt.provider !== sceneObject && (
                                    <span style={{ fontSize: '9px', color: '#666' }} title={`declared on ${opt.provider.name}`}>
                                        {opt.provider.name}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                    {boundTo && (
                        <>
                            <div style={{ height: '1px', backgroundColor: '#3a3a3a', margin: '3px 0' }} />
                            <button
                                onClick={unbind}
                                style={{ ...menuItem, color: '#e88' }}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#333')}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                            >
                                <span style={{ width: '10px' }} />
                                <span>Unbind (keep value)</span>
                            </button>
                        </>
                    )}
                </div>,
                document.body,
            )}
        </div>
    );
};
